import { useState } from 'react'
import { View, Text } from 'react-native'
import { BrandedRefreshControl } from '@/components/ui/BrandedRefreshControl'
import { ScrollView } from 'react-native-gesture-handler'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { useScreenInsets } from '@/lib/hooks/use-screen-insets'
import Animated from 'react-native-reanimated'
import { impact, notification, ImpactStyle, NotificationType } from '@/lib/utils/haptics'
import {
  Plus,
  Car,
  ChevronRight,
  Edit3,
  Trash2,
  Eye,
  EyeOff,
  AlertTriangle,
} from '@/components/ui/icons'
import { Image } from '@/components/ui/Image'
import { Pressable } from '@/components/ui/Pressable'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { EmptyState } from '@/components/ui/EmptyState'
import { Skeleton } from '@/components/ui/Skeleton'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { useHostVehicles, useListingBlockers } from '@/lib/hooks/use-host-vehicles'
import { useHostingEligibility } from '@/lib/hooks/use-hosting-eligibility'
import { listingBlockersNeedPersonal } from '@/lib/utils/listing-publish-blockers'
import { expiredVehicleDocuments } from '@/lib/utils/vehicle-booking-blockers'
import { useListingWizardGate, useEditListingGate } from '@/lib/hooks/use-open-listing-wizard'
import { ResumeDraftDialog } from '@/components/hosting/ResumeDraftDialog'
import { HostVehicleActions } from '@/components/hosting/HostVehicleActions'
import { EditDiscardsDraftDialog } from '@/components/hosting/EditDiscardsDraftDialog'
import { useSemanticColors, SPACING, RADIUS } from '@/lib/theme/tokens'
import { COLORS, FONTS } from '@/lib/brand'
import { WEB_SHADOWS } from '@/lib/theme/web-shadow'
import { useTheme } from '@/lib/hooks/use-theme'
import { useEntrance } from '@/lib/hooks/use-entrance'
import { toast } from '@/lib/toast'
import { captureError, errorMessage } from '@/lib/utils/observability'
import { formatPrice } from '@/lib/utils/format'
import { formatSvDateDisplay } from '@/lib/utils/dates'

/** Height of one listing row. The skeleton stands in for exactly this. */
const LISTING_ROW_HEIGHT = 88

function getStatusColor(status: string, colors: ReturnType<typeof useSemanticColors>): string {
  switch (status) {
    case 'active':
      return colors.success
    case 'draft':
      return colors.warning
    case 'paused':
      return colors.textMuted
    case 'pending_review':
      return colors.info
    default:
      return colors.textMuted
  }
}

export default function ListingsScreen() {
  const { t } = useTranslation()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const insets = useScreenInsets()
  const { currentOrganization, isLoading: isOrganizationsLoading } = useHostingEligibility()
  const {
    vehicles,
    isLoading,
    isError,
    isRefetching,
    refetch,
    deleteVehicle,
    isDeleting,
    setStatus,
  } = useHostVehicles(currentOrganization?.id ?? null)
  const enter = useEntrance(!isLoading)
  // Los blockers los decide el servidor (gate canónico), no la app. Mientras
  // cargan la lista queda vacía: la acción de publicar igual está bloqueada por
  // estado, así que nunca se habilita algo por falta de datos.
  const { data: blockersByVehicle } = useListingBlockers()

  const blockersFor = (vehicle: (typeof vehicles)[number]) =>
    blockersByVehicle?.get(vehicle.id) ?? []

  const blockerLabel = (code: string) =>
    t(`hosting.blocker_${code}`, { defaultValue: t('hosting.blocker_generic') })

  // Los `document_type` son los de la base (`tarjeta_circulacion_front`, …).
  // Se traducen acá y no en el util porque el util es la regla, no la copia.
  const documentLabel = (type: string) =>
    t(`hosting.document_${type}`, { defaultValue: t('hosting.document_generic') })
  const formatExpiryDate = (iso: string) => formatSvDateDisplay(iso)
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null)

  // Same door as the dashboard, through the same gate — this screen kept a bare
  // reset() and would have silently discarded a persisted draft (the empty-state
  // CTA IS this button when the host has no cars).
  const wizardGate = useListingWizardGate()
  const handleAddVehicle = wizardGate.open

  // Shared gate: one definition of "editing discards an in-progress draft", so
  // this door cannot drift from the dashboard again.
  const editGate = useEditListingGate()
  const handleEditVehicle = editGate.openForEdit

  const handleCalendar = (vehicleId: string, title: string) => {
    router.push(`/hosting/calendar/${vehicleId}?title=${encodeURIComponent(title)}`)
  }

  const handleToggleStatus = async (vehicleId: string, currentStatus: string) => {
    // Only active ↔ paused from this list. Draft / pending_review must complete
    // documents + Qualitas path via the wizard — never force-publish from here.
    if (currentStatus === 'draft' || currentStatus === 'pending_review') {
      const vehicle = vehicles.find((item) => item.id === vehicleId)
      const blockers = vehicle ? blockersFor(vehicle) : []
      void notification(NotificationType.Warning)
      toast.info(
        blockers.length > 0
          ? `${t('hosting.listing_needs_title')} ${blockers.map(blockerLabel).join(', ')}.`
          : currentStatus === 'pending_review'
            ? t('hosting.publish_blocked_pending_review')
            : t('hosting.publish_blocked_draft'),
      )
      if (listingBlockersNeedPersonal(blockers)) {
        router.push('/profile/verification' as never)
        return
      }
      handleEditVehicle(vehicleId)
      return
    }
    if (currentStatus !== 'active' && currentStatus !== 'paused') {
      toast.info(t('hosting.publish_blocked_pending_review'))
      return
    }

    const newStatus = currentStatus === 'active' ? 'paused' : 'active'
    try {
      await setStatus({ vehicleId, status: newStatus })
      void notification(NotificationType.Success)
      toast.success(
        t(newStatus === 'active' ? 'hosting.vehicle_published' : 'hosting.vehicle_paused'),
      )
    } catch (err) {
      // Surface the real reason instead of a generic 'common.error' toast with no Sentry
      // signal. Surface real reason (RLS, status-machine violation,
      // pending-booking conflict, etc.) and capture.
      void notification(NotificationType.Error)
      toast.error(errorMessage(err, t('common.error')))
      captureError(err, {
        screen: 'hosting/listings',
        action: 'toggle-status',
        vehicleId,
        newStatus,
      })
    }
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return
    // Destructive confirm: answer the tap now. Waiting for the network round
    // trip to buzz makes an irreversible action feel unacknowledged.
    impact(ImpactStyle.Heavy)
    try {
      await deleteVehicle(deleteTarget.id)
      void notification(NotificationType.Success)
      toast.success(t('hosting.vehicle_deleted'))
      setDeleteTarget(null)
    } catch (err) {
      if (__DEV__) console.error('[Listings] delete failed:', err)
      void notification(NotificationType.Error)
      toast.error(t('common.error'))
    }
  }

  // The screen container renders from ONE JSX position. It used to be
  // re-wrapped inside each sibling `return`, so the whole screen was torn
  // down and rebuilt on the organizations loading -> content transition.
  // Only the region below varies now.
  //
  // No ScreenState here on purpose: the gate is `useHostingEligibility`,
  // a derived view over the organizations query with no error branch of its
  // own, and "this host has no organization" is a legitimate content state
  // (personal listings), not an empty one. The vehicles list below keeps its
  // own skeleton/empty handling.
  return (
    <ScreenContainer scroll={false}>
      {isOrganizationsLoading ? (
        <View style={{ padding: SPACING.lg, gap: SPACING.sm }}>
          <Skeleton width="55%" height={28} borderRadius={RADIUS.sm} />
          <Skeleton width="100%" height={LISTING_ROW_HEIGHT} borderRadius={RADIUS.lg} />
          <Skeleton
            width="100%"
            height={LISTING_ROW_HEIGHT}
            borderRadius={RADIUS.lg}
            staggerIndex={1}
          />
        </View>
      ) : (
        <>
          <ScreenHeader
            title={t('hosting.my_listings')}
            right={
              <Pressable
                onPress={handleAddVehicle}
                accessibilityRole="button"
                accessibilityLabel={t('hosting.add_vehicle')}
                testID="listings.add-vehicle"
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 22, // circle = 44 / 2 — meets 44pt min touch target
                  backgroundColor: COLORS.coral.dark,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Plus size={20} color={COLORS.white} strokeWidth={2.5} />
              </Pressable>
            }
          />

          <ScrollView
            contentContainerStyle={{
              // `flexGrow` so the empty/error branches below can claim the
              // whole region between the header and the tab bar and centre in
              // it. Without it the empty state stopped wherever its content
              // ended and left ~400pt of bare background underneath.
              flexGrow: 1,
              paddingHorizontal: SPACING.lg,
              paddingBottom: insets.bottom + SPACING.xl,
            }}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
            refreshControl={<BrandedRefreshControl refreshing={isRefetching} onRefresh={refetch} />}
          >
            {isLoading ? (
              // Same gap as `listings.vehicle-list` below and the same row
              // height, so the swap from skeleton to listings moves nothing.
              <View style={{ gap: SPACING.sm }}>
                {[0, 1, 2].map((row) => (
                  <Skeleton
                    key={row}
                    width="100%"
                    height={LISTING_ROW_HEIGHT}
                    borderRadius={RADIUS.lg}
                    staggerIndex={row}
                  />
                ))}
              </View>
            ) : isError ? (
              // A failed fetch used to fall through to the empty state, so the
              // host was told they had no vehicles when the request had merely
              // failed — and there was nothing to tap.
              <View style={{ flex: 1, justifyContent: 'center' }}>
                <EmptyState
                  title={t('common.error_title')}
                  description={t('common.error_description')}
                  action={{ label: t('common.retry'), onPress: () => void refetch() }}
                />
              </View>
            ) : vehicles.length === 0 ? (
              // `EmptyState.NoVehicles` instead of a bare title/description with
              // a detached button below it: it carries the same coral car tile
              // the dashboard card uses, its own title→description→action
              // rhythm, and the `empty.no_vehicles_*` copy, which is written in
              // vos ("Agregá tu primer vehículo…") unlike `hosting.no_listings_desc`.
              <View style={{ flex: 1, justifyContent: 'center' }} testID="listings.empty">
                <EmptyState.NoVehicles onAddVehicle={handleAddVehicle} />
              </View>
            ) : (
              <View testID="listings.vehicle-list" style={{ gap: SPACING.sm }}>
                {vehicles.map((vehicle, index) => {
                  const primaryImage =
                    vehicle.images?.find((img) => img.isPrimary) ?? vehicle.images?.[0]
                  const statusColor = getStatusColor(vehicle.status, colors)
                  const blockers = vehicle.status === 'active' ? [] : blockersFor(vehicle)
                  // A propósito NO va dentro de `blockers`: eso responde "¿qué
                  // le falta para publicarse?" y se apaga para los activos. Un
                  // papel vencido es lo contrario — solo le pasa a un carro ya
                  // publicado, y es justo el que hay que avisar.
                  const expiredDocs = expiredVehicleDocuments(vehicle)

                  return (
                    <Animated.View key={vehicle.id} collapsable={false} entering={enter(index)}>
                      <View
                        style={{
                          backgroundColor: colors.surface,
                          borderRadius: RADIUS.lg,
                          overflow: 'hidden',
                          ...WEB_SHADOWS.sm,
                        }}
                      >
                        <Pressable
                          onPress={() => {
                            handleEditVehicle(vehicle.id)
                          }}
                          accessibilityRole="button"
                          accessibilityLabel={vehicle.title}
                          testID={`listings.vehicle-${index}`}
                          style={{ flexDirection: 'row' }}
                        >
                          {/* Image */}
                          <View
                            style={{
                              width: 100,
                              height: LISTING_ROW_HEIGHT,
                              backgroundColor: colors.surfaceSubtle,
                            }}
                          >
                            {primaryImage ? (
                              <Image
                                source={primaryImage.url}
                                blurhash={primaryImage.blurhash ?? undefined}
                                width="100%"
                                height="100%"
                                contentFit="cover"
                                accessibilityLabel={vehicle.title}
                                fallback={
                                  <View
                                    style={{
                                      flex: 1,
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      backgroundColor: colors.surfaceSubtle,
                                    }}
                                    accessibilityRole="image"
                                    accessibilityLabel={vehicle.title}
                                  >
                                    <Car size={28} color={colors.textMuted} strokeWidth={1.3} />
                                  </View>
                                }
                              />
                            ) : (
                              <View
                                style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}
                              >
                                <Car size={28} color={colors.textMuted} strokeWidth={1.3} />
                              </View>
                            )}
                          </View>

                          {/* Info */}
                          <View style={{ flex: 1, padding: SPACING.sm, justifyContent: 'center' }}>
                            <Text
                              style={{
                                fontSize: 15,
                                fontFamily: FONTS.bodyBold,
                                color: colors.textPrimary,
                              }}
                              numberOfLines={1}
                            >
                              {vehicle.title}
                            </Text>
                            <View
                              style={{
                                flexDirection: 'row',
                                alignItems: 'center',
                                marginTop: SPACING.xs,
                                gap: SPACING.xs,
                              }}
                            >
                              <View
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: SPACING.xs,
                                  backgroundColor: statusColor,
                                }}
                              />
                              <Text
                                style={{
                                  fontSize: 13,
                                  fontFamily: FONTS.body,
                                  color: colors.textSecondary,
                                }}
                              >
                                {t(`hosting.status_${vehicle.status}`)}
                              </Text>
                            </View>
                            {blockers.length > 0 ? (
                              <Text
                                style={{
                                  fontSize: 12,
                                  lineHeight: 16,
                                  fontFamily: FONTS.body,
                                  color: colors.textAccent,
                                  marginTop: SPACING.xs,
                                }}
                                numberOfLines={2}
                                testID={`listings.blockers-${index}`}
                              >
                                {t('hosting.listing_needs_title')}{' '}
                                {blockers.map(blockerLabel).join(', ')}
                              </Text>
                            ) : null}
                            <Text
                              style={{
                                fontSize: 14,
                                fontFamily: FONTS.bodyBold,
                                color: colors.textPrimary,
                                marginTop: SPACING.xs,
                              }}
                            >
                              {formatPrice(
                                vehicle.category === 'heavy_duty' && vehicle.hourlyRateCents
                                  ? vehicle.hourlyRateCents
                                  : (vehicle.dailyRateCents ?? 0),
                              )}
                              {vehicle.category === 'heavy_duty'
                                ? t('vehicle_detail.per_hour')
                                : t('vehicle_detail.per_day')}
                            </Text>
                          </View>

                          <View style={{ justifyContent: 'center', paddingRight: SPACING.sm }}>
                            <ChevronRight size={18} color={colors.textMuted} strokeWidth={2} />
                          </View>
                        </Pressable>

                        {/*
                          Papeles vencidos: el único estado en el que un carro
                          se ve "Activo" y aun así la base rechaza toda reserva.
                          Va ACÁ, pegado a la tarjeta del carro y no en un
                          correo, porque es acá donde el anfitrión ya está
                          parado, y va con la fecha y con el botón que lleva
                          directo al paso de documentos — no al paso 1.
                        */}
                        {expiredDocs.length > 0 && (
                          <Pressable
                            onPress={() => handleEditVehicle(vehicle.id, 'documents')}
                            accessibilityRole="button"
                            accessibilityLabel={t('hosting.expired_doc_cta')}
                            testID={`listings.expired-doc-${index}`}
                            style={{
                              flexDirection: 'row',
                              alignItems: 'center',
                              gap: SPACING.sm,
                              paddingVertical: SPACING.sm,
                              paddingHorizontal: SPACING.md,
                              borderTopWidth: 1,
                              borderTopColor: colors.border,
                              backgroundColor: `${COLORS.coral.DEFAULT}12`,
                            }}
                          >
                            <AlertTriangle
                              size={16}
                              color={COLORS.coral.DEFAULT}
                              strokeWidth={2.2}
                            />
                            <View style={{ flex: 1 }}>
                              <Text
                                style={{
                                  fontSize: 13,
                                  fontFamily: FONTS.bodyBold,
                                  color: colors.textAccent,
                                }}
                              >
                                {t('hosting.expired_doc_title')}
                              </Text>
                              <Text
                                style={{
                                  fontSize: 12,
                                  fontFamily: FONTS.body,
                                  color: colors.textSecondary,
                                  marginTop: 2,
                                }}
                              >
                                {t('hosting.expired_doc_desc', {
                                  document: documentLabel(expiredDocs[0]!.documentType),
                                  date: formatExpiryDate(expiredDocs[0]!.expiryDate),
                                })}
                              </Text>
                            </View>
                            <Text
                              style={{
                                fontSize: 13,
                                fontFamily: FONTS.bodyBold,
                                color: colors.textAccent,
                              }}
                              numberOfLines={1}
                            >
                              {t('hosting.expired_doc_cta')}
                            </Text>
                          </Pressable>
                        )}

                        <HostVehicleActions
                          index={index}
                          onEdit={() => handleEditVehicle(vehicle.id)}
                          onCalendar={() => handleCalendar(vehicle.id, vehicle.title)}
                        />
                        {/* Secondary management actions, separate from everyday editing. */}
                        <View
                          style={{
                            flexDirection: 'row',
                            borderTopWidth: 1,
                            borderTopColor: colors.border,
                            paddingVertical: SPACING.sm,
                            paddingHorizontal: SPACING.md,
                          }}
                        >
                          <Pressable
                            onPress={() => handleToggleStatus(vehicle.id, vehicle.status)}
                            accessibilityRole="button"
                            accessibilityLabel={
                              vehicle.status === 'active'
                                ? t('hosting.pause_vehicle')
                                : vehicle.status === 'paused'
                                  ? t('hosting.publish_vehicle')
                                  : t('hosting.continue_listing')
                            }
                            testID={`listings.toggle-status-${index}`}
                            style={{ flex: 1, alignItems: 'center', gap: SPACING.xs - 1 }}
                          >
                            {vehicle.status === 'active' ? (
                              <>
                                <View
                                  style={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 18,
                                    backgroundColor: `${colors.warning}12`,
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <EyeOff size={16} color={colors.warning} strokeWidth={2} />
                                </View>
                                <Text
                                  style={{
                                    fontSize: 12,
                                    fontFamily: FONTS.body,
                                    color: colors.warning,
                                  }}
                                  numberOfLines={1}
                                >
                                  {t('hosting.pause_vehicle')}
                                </Text>
                              </>
                            ) : vehicle.status === 'paused' ? (
                              <>
                                <View
                                  style={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 18,
                                    backgroundColor: `${colors.success}12`,
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <Eye size={16} color={colors.success} strokeWidth={2} />
                                </View>
                                <Text
                                  style={{
                                    fontSize: 12,
                                    fontFamily: FONTS.body,
                                    color: colors.success,
                                  }}
                                  numberOfLines={1}
                                >
                                  {t('hosting.publish_vehicle')}
                                </Text>
                              </>
                            ) : (
                              <>
                                <View
                                  style={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 18,
                                    backgroundColor: `${colors.info}12`,
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <Edit3 size={16} color={colors.info} strokeWidth={2} />
                                </View>
                                <Text
                                  style={{
                                    fontSize: 12,
                                    fontFamily: FONTS.body,
                                    color: colors.info,
                                  }}
                                  numberOfLines={1}
                                >
                                  {t('hosting.continue_listing')}
                                </Text>
                              </>
                            )}
                          </Pressable>
                          <Pressable
                            onPress={() =>
                              setDeleteTarget({ id: vehicle.id, title: vehicle.title })
                            }
                            accessibilityRole="button"
                            accessibilityLabel={t('common.delete')}
                            testID={`listings.delete-${index}`}
                            style={{ flex: 1, alignItems: 'center', gap: SPACING.xs - 1 }}
                          >
                            <View
                              style={{
                                width: 36,
                                height: 36,
                                borderRadius: 18,
                                backgroundColor: `${colors.error}12`,
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Trash2 size={16} color={colors.textError} strokeWidth={2} />
                            </View>
                            <Text
                              style={{
                                fontSize: 12,
                                fontFamily: FONTS.body,
                                color: colors.textError,
                              }}
                              numberOfLines={1}
                            >
                              {t('common.delete')}
                            </Text>
                          </Pressable>
                        </View>
                      </View>
                    </Animated.View>
                  )
                })}
              </View>
            )}
          </ScrollView>

          {/* Delete confirmation modal. */}
          <ConfirmDialog
            visible={!!deleteTarget}
            title={t('hosting.delete_vehicle_title')}
            message={deleteTarget?.title}
            confirmLabel={t('common.delete')}
            cancelLabel={t('common.cancel')}
            onConfirm={handleConfirmDelete}
            onCancel={() => setDeleteTarget(null)}
            confirmLoading={isDeleting}
            icon={<Trash2 size={32} color={colors.textError} strokeWidth={1.5} />}
            testIDs={{
              overlay: 'listings.delete-modal-overlay',
              cancel: 'listings.delete-cancel',
              confirm: 'listings.delete-confirm',
            }}
          />
        </>
      )}
      <ResumeDraftDialog {...wizardGate.dialogProps} testIDPrefix="listings" />

      <EditDiscardsDraftDialog {...editGate.dialogProps} testIDPrefix="listings" />
    </ScreenContainer>
  )
}
